// Shared low-poly wetland parts (water, wetland, tundra & taiga creatures) — a
// deterministic jitter stream (never Math.random, so a variant always rebuilds
// byte-identical geometry), a mirrored eye pair, and a leg builder that follows
// the project's "legs run from the foot on the ground up into the body centre"
// rule, with optional pale stockings (moose, musk-ox), paw/hoof balls (lynx
// snowshoes) and stilt proportions (heron, flamingo). Ported from age-of-mcp's
// v2/wetland-parts.ts: the pure geometry is kept verbatim, with the Part type
// taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

/** Deterministic 0..1 hash stream keyed by (seed, n). */
export function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}

/** Linear blend between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** A mirrored pair of small round eyes at z = ±`z`. */
export function eyes(
  x: number, y: number, z: number,
  r = 0.004, color: THREE.ColorRepresentation = "#14181c",
): Part[] {
  const parts: Part[] = [];
  for (const zz of [z, -z]) {
    parts.push({
      geometry: new THREE.IcosahedronGeometry(r, 0), color,
      position: [x, y, zz],
    });
  }
  return parts;
}

export interface LegOpts {
  /** Height of a pale lower "stocking" segment (moose, musk-ox). */
  lowerH?: number;
  lowerColor?: THREE.ColorRepresentation;
  /** Radius of a paw / hoof ball squashed onto the foot. */
  paw?: number;
  pawColor?: THREE.ColorRepresentation;
  /** Foot-end radius override (defaults to a slight taper of `r`). */
  rFoot?: number;
}

/**
 * One leg standing at (x, 0, z), rising to height `h` — per the project rule
 * the top buries into the torso (h = body-centre height) so the joint never
 * shows. Low-poly: 4-sided cylinders, an optional two-tone stocking split and
 * an optional squashed paw ball at the foot.
 */
export function leg(
  x: number, z: number, h: number, r: number,
  color: THREE.ColorRepresentation, opts: LegOpts = {},
): Part[] {
  const parts: Part[] = [];
  const rFoot = opts.rFoot ?? r * 0.82;
  if (opts.lowerH !== undefined && opts.lowerColor !== undefined && opts.lowerH < h) {
    const lh = opts.lowerH;
    parts.push(
      { geometry: new THREE.CylinderGeometry(rFoot * 1.02, rFoot, lh, 4),
        color: opts.lowerColor, position: [x, lh / 2, z] },
      { geometry: new THREE.CylinderGeometry(r, rFoot * 1.02, h - lh, 4),
        color, position: [x, lh + (h - lh) / 2, z] },
    );
  } else {
    parts.push({ geometry: new THREE.CylinderGeometry(r, rFoot, h, 4),
      color, position: [x, h / 2, z] });
  }
  if (opts.paw !== undefined) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(opts.paw, 0),
      color: opts.pawColor ?? color, position: [x, opts.paw * 0.65, z],
      scale: [1.3, 0.7, 1.05] });
  }
  return parts;
}

/** Four legs mirrored at (±dx, ±dz), all sharing one spec. */
export function quadLegs(
  dx: number, dz: number, h: number, r: number,
  color: THREE.ColorRepresentation, opts: LegOpts = {},
): Part[] {
  const parts: Part[] = [];
  for (const x of [dx, -dx]) {
    for (const z of [dz, -dz]) parts.push(...leg(x, z, h, r, color, opts));
  }
  return parts;
}
